import { Button } from "@/components/ui/button";
import { Car, ArrowRight, DollarSign, Shield, CreditCard, TrendingUp, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { VehicleFinancialProfile, fmt } from "@/hooks/useVehicleSim";
import { motion } from "framer-motion";

interface Props {
  profile: VehicleFinancialProfile;
  onStart: () => void;
}

const LEARNING_OBJECTIVES = [
  "Assess whether you can truly afford a vehicle right now",
  "Compare new vs. used options and understand depreciation",
  "Negotiate price and evaluate financing terms",
  "Identify hidden costs: insurance, maintenance, taxes",
  "Make a purchase decision that protects your financial health",
];

export default function WelcomeScreen({ profile, onStart }: Props) {
  const navigate = useNavigate();
  const readiness = profile.checking + profile.savings >= 5000 && profile.monthlyIncome > profile.monthlyExpenses + 400;

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="glass-card glow-border max-w-md w-full p-8 space-y-6">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto">
            <Car className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground">Vehicle Ownership: Module</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You are about to purchase a vehicle. The right choice can support your goals.
            The wrong choice can delay your progress. Review your finances carefully before moving forward.
          </p>
        </div>

        {/* What You'll Learn */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl border border-accent/20 bg-accent/5 p-4 space-y-2.5"
        >
          <p className="text-xs font-heading font-semibold text-accent uppercase tracking-wider">What You'll Learn</p>
          {LEARNING_OBJECTIVES.map((obj, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
              <span className="text-xs text-foreground/80">{obj}</span>
            </div>
          ))}
        </motion.div>

        <div className="space-y-2">
          <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Your Financial Snapshot</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="glass-card p-3 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-3.5 h-3.5 text-accent" />
                <span className="text-[10px] text-muted-foreground">Cash Available</span>
              </div>
              <p className="font-heading font-bold text-sm text-foreground">{fmt(profile.checking + profile.savings)}</p>
            </div>
            <div className="glass-card p-3 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-accent" />
                <span className="text-[10px] text-muted-foreground">Emergency Fund</span>
              </div>
              <p className="font-heading font-bold text-sm text-foreground">{fmt(profile.emergencyFund)}</p>
            </div>
            <div className="glass-card p-3 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-accent" />
                <span className="text-[10px] text-muted-foreground">Monthly Income</span>
              </div>
              <p className="font-heading font-bold text-sm text-foreground">{fmt(profile.monthlyIncome)}</p>
            </div>
            <div className="glass-card p-3 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-accent" />
                <span className="text-[10px] text-muted-foreground">Credit Score</span>
              </div>
              <p className="font-heading font-bold text-sm text-foreground">{profile.creditScore}</p>
            </div>
          </div>
          <div className={`text-center text-xs font-semibold px-3 py-1.5 rounded-full ${readiness ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'}`}>
            {readiness ? '✓ Financially ready to explore purchasing' : '⚠ Limited financial flexibility — proceed carefully'}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button onClick={onStart} className="w-full gap-2">Begin Module <ArrowRight className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={() => navigate("/")} className="w-full">Back to Dashboard</Button>
        </div>
      </div>
    </div>
  );
}
